import { Controller } from "@hotwired/stimulus";
import { httpRequest } from "../http";
import debounce from "lodash/debounce";

export default class extends Controller {
  static values = {
    deleteUrl: String,
    updateUrl: String,
  };

  connect() {
    // Set up debounced save for input changes
    this._debouncedSave = debounce(() => this._saveContribution(), 500);

    // Add change listeners to all form inputs
    this.element
      .querySelectorAll("input, select, textarea")
      .forEach((field) => {
        field.addEventListener("change", () => this._debouncedSave());
        field.addEventListener("blur", () => this._debouncedSave());
      });
  }

  disconnect() {
    if (this._debouncedSave) {
      this._debouncedSave.cancel();
    }
  }

  delete() {
    if (!confirm("Are you sure you want to delete this contribution?")) {
      return;
    }

    httpRequest(this.deleteUrlValue, { method: "DELETE" })
      .then(() => {
        this.element.remove();
        this.dispatch("contributionDeleted");
      })
      .catch(() => alert("There was a problem deleting the contribution"));
  }

  _saveContribution() {
    const formData = new FormData();

    // Collect all form field values
    this.element
      .querySelectorAll("input, select, textarea")
      .forEach((field) => {
        if (field.name) {
          formData.append(field.name, field.value);
        }
      });

    httpRequest(this.updateUrlValue, {
      method: "PATCH",
      body: formData,
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((data) => {
            throw { status: response.status, data };
          });
        }
        // Clear any previous errors on success
        this._clearErrors();
        return response;
      })
      .catch((error) => {
        if (error.status === 422 && error.data && error.data.errors) {
          this._displayErrors(error.data.errors);
        } else {
          console.error("Error saving contribution:", error);
        }
      });
  }

  _displayErrors(errors) {
    // Clear previous errors first
    this._clearErrors();

    // Display errors for each field
    Object.keys(errors).forEach((fieldName) => {
      const errorMessages = errors[fieldName];
      const errorSelector = `.${fieldName}-error`;
      const errorElement = this.element.querySelector(errorSelector);

      if (errorElement) {
        // Display the first error message
        errorElement.textContent = errorMessages[0];
        errorElement.classList.remove("hidden");

        // Add error styling to the input field
        const fieldSelector = `[data-contribution-field="${fieldName}"]`;
        const inputField = this.element.querySelector(fieldSelector);
        if (inputField) {
          inputField.classList.add("border-red-500", "bg-red-50");
        }
      }
    });
  }

  _clearErrors() {
    // Clear all error messages
    this.element
      .querySelectorAll("[class*='-error']")
      .forEach((errorElement) => {
        errorElement.classList.add("hidden");
        errorElement.textContent = "";
      });

    // Remove error styling from all input fields
    this.element
      .querySelectorAll("input, select, textarea")
      .forEach((field) => {
        field.classList.remove("border-red-500", "bg-red-50");
      });
  }
}
